import { axiosInstance } from '@/utils/libs/axios';
import _ from 'lodash';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const testPlanSchema = z
  .object({
    id: z.string().optional(),
    code: z.string().optional(),
    plan_name: z
      .string()
      .refine(value => value.trim().length > 0 || !_.isEmpty(value), {
        message: 'Test Plan Name is required, please enter test plan name',
      }),
    description: z.string().optional(),
    assignment_time: z.string().optional(),
    expiration_time: z.string().optional(),
    project_id: z.string().optional(),
  })
  .refine(
    data => {
      const assignmentTime = new Date(data.assignment_time ?? '');
      const expirationTime = new Date(data.expiration_time ?? '');

      if (
        (data.assignment_time && data.expiration_time) ||
        !data.assignment_time
      ) {
        return assignmentTime <= expirationTime;
      }
      return true;
    },
    {
      message:
        'Assignment time must be a date before or equal to expiration time',
      path: ['assignment_time'],
    },
  )
  .refine(
    data => {
      const assignmentTime = new Date(data.assignment_time ?? '');
      const expirationTime = new Date(data.expiration_time ?? '');

      if (
        (data.assignment_time && data.expiration_time) ||
        !data.expiration_time
      ) {
        return expirationTime >= assignmentTime;
      }
      return true;
    },
    {
      message:
        'Expiration time must be a date after or equal to assignment time',
      path: ['expiration_time'],
    },
  );

export const importFileSchema = z.object({
  file: z.any().refine(file => !_.isEmpty(file), 'File is Required'),
});

export type ITestPlanSchema = z.infer<typeof testPlanSchema>;

export type IImportFileSchema = z.infer<typeof importFileSchema>;

export const TestPlanService = {
  useCreate: () =>
    useMutation({
      mutationFn: async (data: ITestPlanSchema) => {
        const response = await axiosInstance.post('api/crud/test-plan', data);
        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(
          `/api/crud/test-plan/${id}`,
        );
        return response.data;
      },
    }),

  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-plan/${id}/archive`,
        );
        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-plan/${id}/unarchive`,
        );

        return response.data;
      },
    }),

  useGet: (id?: string) =>
    useQuery<ITestPlanSchema>({
      queryKey: ['test-plan', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(`/api/crud/test-plan/${id}`);
        return response.data?.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ITestPlanSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-plan/${data.id}`,
          data,
        );
        return response.data;
      },
    }),

  useImport: (id: string) =>
    useMutation({
      mutationFn: async (data: IImportFileSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-plan/${id}/import`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
      },
    }),

  useExport: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.get(
          `/api/crud/test-plan/${id}/export`,
          {
            responseType: 'blob',
          },
        );

        return response.data;
      },
    }),
};
